import React from 'react';
import { Dialog, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CloseIcon from '@mui/icons-material/Close';
import * as api from '../plannerApi';
import * as T from '../types';
import { RECURRING_FREQUENCIES, amount } from '../labels';
import { useNotify, useRun } from '../common';

/**
 * "Passo a prever?": an item added by hand that the app did not see coming. One tap makes it a regular purchase
 * or gives it a minimum at home; "Não" stops asking for it.
 */
export const PredictionDialog: React.FC<{ prediction: T.ShoppingPrediction | null; onClose: (changed: boolean) => void }> = ({ prediction, onClose }) => {
  const run = useRun();
  const notify = useNotify();
  if (!prediction) return null;
  const { recurring, minStock } = prediction;

  const apply = async (kind: T.ShoppingPredictionKind, done: string) => {
    const ok = await run(() => api.applyShoppingPrediction(prediction.catalogItemPublicId, kind));
    if (ok) notify(`${prediction.name}: ${done}`, { severity: 'success' });
    onClose(!!ok);
  };

  const dismiss = async () => {
    await run(() => api.dismissShoppingPrediction(prediction.catalogItemPublicId));
    onClose(false);
  };

  const frequency = recurring ? RECURRING_FREQUENCIES.find(f => f.value === recurring.frequency)?.text : undefined;
  const each = recurring?.quantity ? `, ${amount(recurring.quantity, recurring.unit, false)} de cada vez` : '';

  return (
    <Dialog open onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Prever {prediction.name}?</DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 0.5 }}>
        {prediction.timesAdded > 1 ? `Juntaste-o à mão ${prediction.timesAdded} vezes.` : 'Juntaste-o à mão.'}
      </Typography>
      <List>
        {recurring && (
          <ListItemButton onClick={() => apply('Recurring', 'compra regular')}>
            <ListItemIcon><EventRepeatIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Compra regular" secondary={`${frequency}${each}`} />
          </ListItemButton>
        )}
        {minStock && (
          <ListItemButton onClick={() => apply('MinStock', 'mínimo em casa')}>
            <ListItemIcon><Inventory2Icon /></ListItemIcon>
            <ListItemText primary="Mínimo em casa" secondary={`Junta à lista quando houver menos de ${amount(minStock.minStock, minStock.unit, false)}`} />
          </ListItemButton>
        )}
        <ListItemButton onClick={dismiss}>
          <ListItemIcon><CloseIcon /></ListItemIcon>
          <ListItemText primary="Não, não perguntes mais" />
        </ListItemButton>
      </List>
    </Dialog>
  );
};
